/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useState } from 'react';
import ParameterWidget from '../../components/ParameterWidget';
import {
  PriorityList,
  SimplifiedParameterWidget,
  DashboardDataFilter,
  ParametersType,
  TimeSpan,
  DeviceType
} from '../../constants/telosair-interfaces';
import { getPriorityColor, getPriorityText } from '../../_helpers/dashboardHelper';
import HistogramContainer from './histogramContainer';
import MapContainer from './mapContainer';
import Deque from '../../classes/deque';
import InsightsContainer from './Insights';
import { deviceFilter, getParameterName } from '../../_helpers/globalHelper';
import { useUnifiedData } from '../../hooks/useUnifiedData';

interface SimplifiedPageProps {
  dataFilter: DashboardDataFilter | null;
  extendedLabel: string;
  timeSpan: TimeSpan;
  setSelectedParameter: (parameter: ParametersType[]) => void;
  lineChartGraphData: Record<ParametersType, Deque>;
  loading: boolean;
  setDataFilter?: (filter: DashboardDataFilter | null) => void;
}

export default function SimplifiedPage(props: SimplifiedPageProps): ReactElement {
  const {
    parameters,
    groupDataSummary,
    dataSummary,
    deviceDataSummary,
    clientThresholdSettings,
    userConfigDataV2,
    loading: devicesLoading,
    groupedData,
    groupTypes,
    devices
  } = useUnifiedData();
  const {
    dataFilter,
    extendedLabel,
    timeSpan,
    setSelectedParameter,
    lineChartGraphData,
    loading,
    setDataFilter
  } = props;

  const [parameterData, setParameterData] = useState<SimplifiedParameterWidget[][] | null>([]);
  const [expanded, setExpanded] = useState<'map' | 'histogram'>('map');
  const [deviceType, setDeviceType] = useState<DeviceType>(DeviceType.MIXED);

  useEffect(() => {
    const { data, type } = deviceFilter(
      dataFilter,
      groupDataSummary,
      deviceDataSummary,
      dataSummary,
      devices,
      groupTypes
    );
    setDeviceType(type);
    const mappedParameters = parameters
      ?.sort((a, b) => a.order - b.order)
      .map((parameter) => ({
        value: data?.[parameter.parameterName] ?? '-',
        label: getParameterName(parameter, type)?.displayName,
        shortName: getParameterName(parameter, type)?.shortName,
        unit:
          (userConfigDataV2?.config?.unit &&
            userConfigDataV2?.config?.unit[parameter.parameterName]) ||
          '',
        description: getPriorityText(
          parameter.parameterName,
          data?.[parameter.parameterName] ?? null,
          clientThresholdSettings,
          userConfigDataV2,
          type
        ),
        status:
          data?.[parameter.parameterName] != null
            ? getPriorityColor(
                parameter.parameterName,
                data[parameter.parameterName],
                clientThresholdSettings,
                userConfigDataV2,
                type
              )
            : PriorityList.none
      }));

    const parameterChunks: SimplifiedParameterWidget[][] = [];
    if (mappedParameters?.length > 0) {
      parameterChunks.push([mappedParameters[0]]);
    }
    if (mappedParameters?.length > 1) {
      parameterChunks.push([mappedParameters[1]]);
    }

    if (mappedParameters?.length > 2) {
      parameterChunks.push([mappedParameters[2]]);
    }
    if (mappedParameters?.length > 3) {
      parameterChunks.push(mappedParameters.slice(3));
    }

    setParameterData(parameterChunks);
  }, [
    dataFilter,
    groupDataSummary,
    deviceDataSummary,
    dataSummary,
    clientThresholdSettings,
    userConfigDataV2,
    parameters,
    groupTypes,
    devices
  ]);

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto h-full">
      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
          {parameterData &&
            parameterData.map((parameter, index) => (
              <ParameterWidget
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                parameters={parameter}
              />
            ))}
        </div>
      </div>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4 w-full">
        <MapContainer
          expanded={expanded === 'map'}
          setExpanded={setExpanded}
          extendedLabel={extendedLabel}
          groups={groupedData}
          setDataFilter={setDataFilter}
          loading={devicesLoading}
          dataFilter={dataFilter}
        />
        <div
          className={`flex flex-1 flex-col gap-4 col-span-1 ${
            expanded === 'histogram' ? 'md:col-span-3' : 'md:col-span-2'
          }`}
        >
          <HistogramContainer
            expanded={expanded === 'histogram'}
            setExpanded={setExpanded}
            extendedLabel={extendedLabel}
            timeSpan={timeSpan}
            onChangeParameter={setSelectedParameter}
            lineChartGraphData={lineChartGraphData}
            loading={loading}
            type={deviceType}
          />
          <InsightsContainer
            lineChartGraphData={lineChartGraphData}
            expanded={expanded === 'histogram'}
            loading={loading}
          />
        </div>
      </div>
      <div className="h-64 md:h-0" />
    </div>
  );
}
